class SterlingCollectionVideo extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.videos = [...this.querySelectorAll('video')];
    this.toggle = this.querySelector('[data-video-toggle]');
    if (!this.videos.length || !this.toggle) return;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.userPaused = false;
    this.userStarted = false;
    this.inView = false;
    this.failedVideos = new WeakSet();
    this.failedSources = new WeakSet();
    this.update = () => this.sync();
    this.motionQuery.addEventListener('change', this.update);
    this.mobileQuery.addEventListener('change', this.update);
    document.addEventListener('visibilitychange', this.update, { signal });
    this.toggle.addEventListener('click', () => {
      this.userPaused = Boolean(this.activeVideo && !this.activeVideo.paused);
      this.userStarted = !this.userPaused;
      this.sync();
    }, { signal });
    for (const video of this.videos) {
      video.addEventListener('playing', () => {
        if (video !== this.activeVideo || !this.shouldPlay()) {
          video.pause();
          return;
        }
        this.dataset.ready = video.dataset.variant;
        this.updateControl();
      }, { signal });
      video.addEventListener('pause', () => this.updateControl(), { signal });
      const fail = () => {
        this.failedVideos.add(video);
        video.pause();
        if (video === this.activeVideo) {
          delete this.dataset.ready;
          this.toggle.hidden = true;
        }
      };
      video.addEventListener('error', fail, { signal });
      const sources = [...video.querySelectorAll('source')];
      for (const source of sources) {
        source.addEventListener('error', () => {
          this.failedSources.add(source);
          // A failed HLS source must not hide a working MP4 fallback.
          if (sources.every((item) => this.failedSources.has(item))) fail();
        }, { signal });
      }
    }
    this.observer = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting;
      this.sync();
    }, { threshold: .05 });
    this.observer.observe(this);
    this.sync();
  }

  shouldPlay() {
    return this.isConnected && this.inView && !document.hidden && !this.motionQuery.matches &&
      !this.userPaused && (!navigator.connection?.saveData || this.userStarted);
  }

  sync() {
    const video = (this.mobileQuery.matches && this.videos.find((item) => item.dataset.variant === 'mobile')) || this.videos[0];
    if (video !== this.activeVideo) {
      for (const item of this.videos) item.pause();
      delete this.dataset.ready;
      this.activeVideo = video;
    }
    this.toggle.hidden = this.motionQuery.matches || this.failedVideos.has(video);
    if (this.motionQuery.matches) delete this.dataset.ready;
    if (!this.shouldPlay() || this.failedVideos.has(video)) {
      video.pause();
      this.updateControl();
      return;
    }
    if (!video.dataset.loaded) {
      for (const source of video.querySelectorAll('[data-src]')) source.src = source.dataset.src;
      if (video.dataset.src) video.src = video.dataset.src;
      video.dataset.loaded = 'true';
      video.muted = true;
      video.load();
    }
    video.play().then(() => {
      if (video !== this.activeVideo || !this.shouldPlay()) video.pause();
    }).catch(() => this.updateControl());
  }

  updateControl() {
    const playing = Boolean(this.activeVideo && !this.activeVideo.paused);
    this.toggle.setAttribute('aria-label', playing ? 'Pause banner animation' : 'Play banner animation');
    this.toggle.querySelector('[data-video-play]').hidden = playing;
    this.toggle.querySelector('[data-video-pause]').hidden = !playing;
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.observer?.disconnect();
    this.motionQuery?.removeEventListener('change', this.update);
    this.mobileQuery?.removeEventListener('change', this.update);
    for (const video of this.videos || []) video.pause();
    delete this.dataset.ready;
  }
}

if (!customElements.get('sterling-collection-video')) {
  customElements.define('sterling-collection-video', SterlingCollectionVideo);
}
